import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { subscribeLiveRefresh } from '../utils/liveRefresh';

/**
 * Call `callback` on an interval while the tab is visible, immediately on
 * focus/resume, and when a foreground push asks screens to refresh.
 * Pass `hiddenIntervalMs` to keep a slower poll running when the tab is hidden
 * (used by the barista console so a new order is not missed).
 */
export function useLivePoll(
  callback: () => void,
  intervalMs: number,
  enabled = true,
  options?: { hiddenIntervalMs?: number }
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const hiddenIntervalMs = options?.hiddenIntervalMs;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const runIfVisible = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      callbackRef.current();
    };
    const runIfHidden = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
      callbackRef.current();
    };
    const runAlways = () => {
      if (cancelled) return;
      callbackRef.current();
    };

    const intervalId = window.setInterval(runIfVisible, intervalMs);
    const hiddenId =
      hiddenIntervalMs && hiddenIntervalMs > 0
        ? window.setInterval(runIfHidden, hiddenIntervalMs)
        : 0;
    document.addEventListener('visibilitychange', runIfVisible);
    window.addEventListener('focus', runAlways);
    const unsubscribeLive = subscribeLiveRefresh(runAlways);

    let capHandle: { remove: () => Promise<void> } | undefined;
    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
        if (isActive) runAlways();
      }).then((handle: { remove: () => Promise<void> }) => {
        if (cancelled) {
          void handle.remove();
        } else {
          capHandle = handle;
        }
      });
    }

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (hiddenId) window.clearInterval(hiddenId);
      document.removeEventListener('visibilitychange', runIfVisible);
      window.removeEventListener('focus', runAlways);
      unsubscribeLive();
      void capHandle?.remove();
    };
  }, [intervalMs, enabled, hiddenIntervalMs]);
}
